import { clearUndefined, isDefined, isNotDefined } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { getPreviousValue, getTcValidation } from '@/common/trade-capture/tradeCaptureValidation.ts';
import type {
  CheckedFlexiForward,
  CheckedFlexiForwardLeg,
  CheckFlexiForwardBody,
  CheckFlexiForwardParams,
  CheckFlexiForwardResponse,
} from '@/store/flexi-forward/api/trade-capture/checkFlexiForward.model.ts';
import type { FlexiForwardProperties, FlexiForwardTradeCaptureError, Solvable } from '@/store/flexi-forward/state.ts';
import type { TcValidation, TradeCaptureChecked, TradeCaptureError } from '@/store/types.ts';
import type { TradeCaptureResponseLegErrors } from '@/types/trade-capture.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

export type FlexiForwardTcResponse =
  | {
      type: 'success';
      tradeCapture: TradeCaptureChecked<FlexiForwardProperties, FlexiForwardTradeCaptureError>;
      properties: FlexiForwardProperties;
    }
  | {
      type: 'error';
      tradeCapture: TradeCaptureError;
    };

export const checkFlexiForward = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<FlexiForwardTcResponse, CheckFlexiForwardParams>({
    query: (params) => ({
      url: '/tradeCaptureBatch/FxAmericanForward',
      method: 'POST',
      body: getCheckFlexiForwardBody(params),
    }),
    transformResponse: (response: CheckFlexiForwardResponse, _meta, arg): FlexiForwardTcResponse => {
      const tileId = arg.tileId;
      const sessionId = arg.tradeCapture.sessionId;
      const notionalCurrencyIndexState = arg.flexiForwardState?.properties.notionalCurrencyIndex;

      const responseFlexiForward = response?.[tileId];

      if (isNotDefined(responseFlexiForward)) {
        const tradeCaptureError: TradeCaptureError = {
          status: 'ERROR',
          error: 'SERVER_ERROR',
          sessionId,
          version: -1,
        };
        return { type: 'error', tradeCapture: tradeCaptureError };
      }

      const changedFields = responseFlexiForward?.changedFields;

      const validation: FlexiForwardTradeCaptureError = getFlexiForwardValidation(
        responseFlexiForward.errors?.legs?.[0],
        responseFlexiForward.warnings?.legs?.[0],
      );

      const properties = getFlexiForwardProperties(changedFields, notionalCurrencyIndexState, validation);

      return {
        type: 'success',
        properties: clearUndefined(properties),
        tradeCapture: {
          status: 'CHECKED',
          sessionId,
          version: responseFlexiForward?.idVersion ?? 0,

          isReadyToPrice: changedFields?.isReadyToPrice ?? false,
          isPriceObsolete: changedFields?.isPriceObsolete ?? false,
          isProductObsolete: changedFields?.isProductObsolete ?? false,

          validation,
        },
      };
    },
  });
};

const getCheckFlexiForwardBody = (params: CheckFlexiForwardParams): CheckFlexiForwardBody => {
  const { tileId, tradeCapture, properties } = params;

  const notionalAmount =
    properties.notional !== undefined && isDefined(properties.notionalCurrencyIndex)
      ? getAmountWithCurrency(properties.notional, properties.notionalCurrencyIndex, 'amount')
      : undefined;

  const upfront = properties.upfront !== undefined ? (properties.upfront?.toString() ?? null) : undefined;

  const rate = properties.rate !== undefined ? (properties.rate?.toString() ?? null) : undefined;

  const hedgeType = properties.hedgeType === 'Live' ? 'None' : 'Spot';

  const hedgeAmount =
    properties.deltaHedgeAmount !== undefined && isDefined(properties.deltaHedgeCurrencyIndex)
      ? getAmountWithCurrency(properties.deltaHedgeAmount, properties.deltaHedgeCurrencyIndex, 'hedgeAmountInCcy')
      : undefined;

  const hedgePriceString =
    properties.spotRef !== undefined ? (properties.spotRef !== null ? properties.spotRef.toString() : '') : undefined;

  return [
    {
      productType: 'FxAmericanForward',
      tileId,
      sessionId: tradeCapture.sessionId,
      request: {
        idVersion: tradeCapture.version + 1,

        changedFields: {
          currencyPair: properties.currencyPair ?? undefined,
          markupCurrency: properties.marginCurrency ?? undefined,
          hedgeType: isDefined(properties.hedgeType) ? hedgeType : undefined,
          ...hedgeAmount,
          hedgePriceString,

          legs: {
            0: {
              callabilityStartString: properties.startDate ?? undefined,
              cutOffMarketPlace: properties.cutOffMarketPlace ?? undefined,
              deliveryDateString: properties.deliveryDate ?? undefined,
              expiryDateString: properties.expiryDate ?? undefined,
              forwardRateString: rate,
              ...notionalAmount,
              markupCurrency: properties.marginCurrency ?? undefined,
              premiumPaymentAmountAskString: upfront,
              premiumPaymentAmountBidString: upfront,
              premiumPaymentDateString: properties.premiumDate ?? undefined,
              premiumPaymentCurrency: properties.upfrontCurrency ?? undefined,
              productName: 'FxAmericanForward',
              side: properties?.clientsWay ?? undefined,
            },
          },
        },
      },
    },
  ];
};

function getFlexiForwardProperties(
  changedFields: CheckedFlexiForward['changedFields'] | undefined,
  notionalCurrencyIndexState: CurrencyIndex | undefined,
  validation: FlexiForwardTradeCaptureError,
): FlexiForwardProperties {
  const flexiForwardLeg = changedFields?.legs[0];

  if (isNotDefined(flexiForwardLeg)) {
    return {};
  }

  const { amount1, amount2 } = flexiForwardLeg;
  const negotiatedCurrency = notionalCurrencyIndexState ?? flexiForwardLeg.negotiatedCurrency;
  const notionalField = getNotionalAmount({ amount1, amount2, negotiatedCurrency }, validation?.notional);

  const deltaHedge = getDeltaHedgeValueWIthCurrency(flexiForwardLeg);

  const { premiumPaymentAmountBid, premiumPaymentAmountAsk } = flexiForwardLeg;

  const upfront =
    premiumPaymentAmountBid !== undefined || premiumPaymentAmountAsk !== undefined
      ? premiumPaymentAmountBid !== undefined
        ? premiumPaymentAmountBid
        : premiumPaymentAmountAsk
      : undefined;

  const { forwardRate, solvable } = flexiForwardLeg;

  const upfrontAndRate = getUpfrontAndRateValues(upfront, forwardRate, solvable);

  const hedgeType = flexiForwardLeg.hedgeType === 'None' ? 'Live' : 'Spot';

  return {
    callabilityType: flexiForwardLeg?.callabilityType,
    clientsWay: flexiForwardLeg?.side,
    currency1: flexiForwardLeg?.currency1,
    currency2: flexiForwardLeg?.currency2,
    currencyPair: flexiForwardLeg?.currencyPair,
    cutOffMarketPlace: flexiForwardLeg?.cutOffMarketPlace,
    deliveryDate: flexiForwardLeg?.deliveryDate ?? getPreviousValue(validation?.deliveryDate),
    deliveryDateTenor: flexiForwardLeg?.deliveryDateTenor,
    expiryDate: flexiForwardLeg?.expiryDate ?? getPreviousValue(validation?.expiryDate),
    expiryDateTenor: flexiForwardLeg?.expiryDateTenor,
    hedgeType: isDefined(flexiForwardLeg.hedgeType) ? hedgeType : undefined,
    marginCurrency: flexiForwardLeg?.markupCurrency,
    ...notionalField,
    possibleCutOffMarketPlaces: flexiForwardLeg?.possibleCutOffMarketPlaces,
    ...deltaHedge,
    premiumDate: flexiForwardLeg?.premiumPaymentDate ?? getPreviousValue(validation?.premiumDate),
    premiumDateTenor: flexiForwardLeg?.premiumPaymentDateTenor,
    ...upfrontAndRate,
    upfrontCurrency: flexiForwardLeg?.premiumPaymentCurrency,
    spotHedge: flexiForwardLeg.hedgePaymentDate ?? undefined,
    spotRef: flexiForwardLeg?.hedgePrice ?? changedFields?.hedgePrice,
    startDate: flexiForwardLeg?.callabilityStart ?? getPreviousValue(validation?.startDate),
    startDateTenor: flexiForwardLeg?.callabilityStartTenor,
  };
}

function getFlexiForwardValidation(
  errors: TradeCaptureResponseLegErrors | undefined,
  warnings: TradeCaptureResponseLegErrors | undefined,
): FlexiForwardTradeCaptureError {
  const propertyKeys = Object.keys(errors ?? {}).concat(Object.keys(warnings ?? {}));

  return propertyKeys.reduce((acc, key) => {
    switch (key) {
      case 'amount1':
        acc.notional = getTcValidation(errors?.amount1, warnings?.amount1);
        return acc;
      case 'amount2':
        acc.notional = getTcValidation(errors?.amount2, warnings?.amount2);
        return acc;
      case 'hedgeAmountInCcy1':
        acc.deltaHedgeAmount = getTcValidation(errors?.hedgeAmountInCcy1, warnings?.hedgeAmountInCcy1);
        return acc;
      case 'hedgeAmountInCcy2':
        acc.deltaHedgeAmount = getTcValidation(errors?.hedgeAmountInCcy2, warnings?.hedgeAmountInCcy2);
        return acc;
      case 'premiumPaymentDate':
        acc.premiumDate = getTcValidation(errors?.premiumPaymentDate, warnings?.premiumPaymentDate);
        return acc;
      case 'forwardRate':
        acc.rate = getTcValidation(errors?.forwardRate, warnings?.forwardRate);
        return acc;
      case 'hedgePrice':
        acc.spotRef = getTcValidation(errors?.hedgePrice, warnings?.hedgePrice);
        return acc;
      case 'callabilityStart':
        acc.startDate = getTcValidation(errors?.callabilityStart, warnings?.callabilityStart);
        return acc;
      case 'premiumPaymentAmountBid':
        acc.upfront = getTcValidation(errors?.premiumPaymentAmountBid, warnings?.premiumPaymentAmountBid);
        return acc;
      case 'premiumPaymentAmountAsk':
        acc.upfront = getTcValidation(errors?.premiumPaymentAmountAsk, warnings?.premiumPaymentAmountAsk);
        return acc;
      default: {
        const key1 = key as keyof FlexiForwardProperties;
        acc[key1] = getTcValidation(errors?.[key], warnings?.[key]) ?? [];
        return acc;
      }
    }
  }, {} as FlexiForwardTradeCaptureError);
}

function getNotionalAmount(
  amount: { amount1: number | undefined; amount2: number | undefined; negotiatedCurrency: CurrencyIndex | undefined },
  validation?: TcValidation[],
): { notional: number | undefined; notionalCurrencyIndex: CurrencyIndex | undefined } | undefined {
  const { amount1, amount2 } = amount;

  const negotiatedCurrency = isDefined(amount.negotiatedCurrency)
    ? amount.negotiatedCurrency
    : isDefined(amount1)
      ? 1
      : 2;

  const previousAmountValue = validation?.[0]?.previousValue;

  if (isDefined(previousAmountValue)) {
    return { notional: previousAmountValue, notionalCurrencyIndex: negotiatedCurrency };
  }

  if (isNotDefined(amount1) && isNotDefined(amount2)) {
    return undefined;
  }

  return negotiatedCurrency === 1
    ? { notional: amount1, notionalCurrencyIndex: 1 }
    : { notional: amount2, notionalCurrencyIndex: 2 };
}

function getAmountWithCurrency(
  amount: number | null,
  currencyIndex: CurrencyIndex,
  property: string,
): { [key: string]: string } | undefined {
  if (amount === undefined) {
    return undefined;
  }

  const amountKey = currencyIndex === 1 ? `${property}1String` : `${property}2String`;
  const amountValue = amount === null ? '' : amount.toString();

  return { [amountKey]: amountValue };
}

function nullForString0(value: number | null): number | null | undefined {
  return value === 0 ? null : value;
}

function getUpfrontAndRateValues(
  upfront: number | null | undefined,
  rate: number | null | undefined,
  solvable: Solvable | undefined,
): { upfront: number | null; rate: number | null; solvable: Solvable } | {} {
  if (isDefined(upfront)) {
    return {
      upfront,
      rate: null,
      solvable,
    };
  }

  if (isDefined(rate)) {
    return {
      upfront: null,
      rate: nullForString0(rate),
      solvable,
    };
  }

  return {};
}

function getDeltaHedgeValueWIthCurrency(
  flexiForwardLeg: CheckedFlexiForwardLeg,
): { deltaHedgeAmount: number | null; hedgeCurrencyIndex: CurrencyIndex } | undefined {
  switch (true) {
    case flexiForwardLeg.hedgeAmountInCcy1 !== undefined:
      return { deltaHedgeAmount: flexiForwardLeg.hedgeAmountInCcy1, hedgeCurrencyIndex: 1 };
    case flexiForwardLeg.hedgeAmountInCcy2 !== undefined:
      return { deltaHedgeAmount: flexiForwardLeg.hedgeAmountInCcy2, hedgeCurrencyIndex: 2 };
    default:
      return undefined;
  }
}
